import { cn } from '../../lib/cn'
import type { ClubMark as Mark } from '../../lib/types'

interface ClubMarkProps {
  mark: Mark
  /** `brass` on paper grounds, `gilt` on pine (the membership card). */
  tone?: 'brass' | 'gilt'
  className?: string
}

/**
 * The six marks a member may wear beside their name, drawn as inline SVG.
 *
 * A CLOSED set, chosen rather than uploaded — which is the whole point. No
 * storage, no image pipeline, no moderation surface, and nothing that can
 * outlive a GDPR purge.
 *
 * Plain `brass` IS correct here and must not be "fixed" to brass-deep: these
 * are strokes and fills, not text. §3 of the design system bans brass as a
 * text colour in either direction, and in the same breath keeps it right for
 * rules, crest strokes and hairline borders. This is the latter.
 *
 * Each path has to read as a distinct silhouette at 28px — MemberAvatar's `sm`,
 * which is what the roster table uses. That, not fidelity, is the bar.
 *
 * `initial` renders nothing: MemberAvatar draws the serif letter instead, which
 * is the club's default and what every member has today.
 */
export function ClubMark({ mark, tone = 'brass', className }: ClubMarkProps) {
  if (mark === 'initial') return null

  const stroke = tone === 'gilt' ? 'var(--color-gilt)' : 'var(--color-brass)'

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('block', className)}
    >
      {mark === 'crest' && (
        <>
          <path d="M12 3 20 6v6c0 4-3.4 7.2-8 9-4.6-1.8-8-5-8-9V6z" />
          <path d="M12 8.5v6M9 11.5h6" />
        </>
      )}
      {mark === 'pin' && (
        <>
          <path d="M4 20q8-2 16 0" />
          <path d="M9 20V4" />
          <circle cx="9" cy="3.4" r="1.2" fill={stroke} />
          <path d="M9 5.2 18 8l-9 2.8z" fill={stroke} stroke="none" opacity="0.9" />
        </>
      )}
      {mark === 'ball' && (
        <>
          <circle cx="12" cy="12" r="8" />
          <circle cx="9.4" cy="10" r="0.9" fill={stroke} stroke="none" />
          <circle cx="13" cy="9" r="0.9" fill={stroke} stroke="none" />
          <circle cx="15" cy="12.4" r="0.9" fill={stroke} stroke="none" />
          <circle cx="11" cy="14.4" r="0.9" fill={stroke} stroke="none" />
        </>
      )}
      {mark === 'tee' && (
        <>
          <path d="M6.5 5.5h11l-3.6 4.2v9.3h-3.8V9.7z" />
          <path d="M5 3.6h14" />
        </>
      )}
      {mark === 'bell' && (
        <>
          <path d="M12 3.2a5.6 5.6 0 0 0-5.6 5.6c0 4.2-1.4 5.6-1.4 5.6h14s-1.4-1.4-1.4-5.6A5.6 5.6 0 0 0 12 3.2z" />
          <path d="M10.2 17.2a2 2 0 0 0 3.6 0" />
        </>
      )}
    </svg>
  )
}
